import {
  MEETING_ASSIST_CARD_KINDS,
  type MeetingAssistCardKind,
} from '@littlebird/action-cards';
import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/Switch';
import {
  MEETING_ASSIST_POPUP_RANGES,
  settingsStore,
} from '@/stores/settings.store';
import { userStore } from '@/stores/user.store';

import { NumericInput } from '../numeric-input';
import { SettingsSection } from '../settings-section';

const KIND_LABELS: Record<MeetingAssistCardKind, string> = {
  info: 'Answers',
  todo: 'To-dos',
  draft: 'Drafts',
};

const NUMERIC_SETTINGS = [
  {
    key: 'minImportance',
    label: 'Minimum importance',
    description:
      'Cards rated below this stay in the Assist list; unrated cards pop up',
    step: 1,
  },
  {
    key: 'maxCardAgeSeconds',
    label: 'Maximum card age (seconds)',
    description:
      'An older card, such as one that arrives late, stays in the Assist list',
    step: 10,
  },
  {
    key: 'displaySeconds',
    label: 'Time on screen (seconds)',
    description: 'How long a popup stays up while the pointer is off it',
    step: 1,
  },
] as const;

/**
 * Whether Meeting Assist cards pop up beside the collapsed meeting widget. Internal
 * users also choose which cards pop up, and for how long. The settings stay on
 * this device.
 */
export const MeetingAssistPopupOptions = observer(() => {
  const popup = settingsStore.meetingAssistPopup;
  const toggleKind = (kind: MeetingAssistCardKind) =>
    settingsStore.setMeetingAssistPopup({
      kinds: popup.kinds.includes(kind)
        ? popup.kinds.filter((selected) => selected !== kind)
        : [...popup.kinds, kind],
    });
  const kindsLabel =
    popup.kinds.length === 0
      ? 'None'
      : popup.kinds.map((kind) => KIND_LABELS[kind]).join(', ');

  return (
    <>
      <SettingsSection.Seperator />
      <SettingsSection.Item searchId="meetings.assist-popups">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>Popups</SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Pop new cards up beside the collapsed meeting widget
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <Switch
          aria-label="Pop up Meeting Assist cards"
          checked={popup.enabled}
          onCheckedChange={(enabled) =>
            settingsStore.setMeetingAssistPopup({ enabled })
          }
        />
      </SettingsSection.Item>
      {popup.enabled && userStore.isInternalUser && (
        <>
          <SettingsSection.Item>
            <SettingsSection.ItemHeader>
              <SettingsSection.ItemLabel>
                Cards that pop up
              </SettingsSection.ItemLabel>
            </SettingsSection.ItemHeader>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger aria-label="Cards that pop up">
                <span>{kindsLabel}</span>
                <Icons.chevronDown className="size-3" />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content
                align="end"
                side="bottom"
                className="w-[12rem] gap-1 mt-1"
              >
                {MEETING_ASSIST_CARD_KINDS.map((kind) => (
                  <DropdownMenu.CheckboxItem
                    key={kind}
                    checked={popup.kinds.includes(kind)}
                    onCheckedChange={() => toggleKind(kind)}
                    onSelect={(event) => event.preventDefault()}
                  >
                    {KIND_LABELS[kind]}
                  </DropdownMenu.CheckboxItem>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </SettingsSection.Item>
          {NUMERIC_SETTINGS.map(({ key, label, description, step }) => (
            <SettingsSection.Item key={key}>
              <SettingsSection.ItemHeader>
                <SettingsSection.ItemLabel>{label}</SettingsSection.ItemLabel>
                <SettingsSection.ItemDescription>
                  {description}
                </SettingsSection.ItemDescription>
              </SettingsSection.ItemHeader>
              <NumericInput
                value={popup[key]}
                ariaLabel={label}
                {...MEETING_ASSIST_POPUP_RANGES[key]}
                step={step}
                onCommit={(value) =>
                  settingsStore.setMeetingAssistPopup({ [key]: value })
                }
              />
            </SettingsSection.Item>
          ))}
        </>
      )}
    </>
  );
});
